import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import {
  Plus,
  FileText,
  MathOperations,
  Folder,
  Clock,
  Trash,
  WifiSlash,
  ArrowClockwise,
  Article,
  NotePencil,
  SquaresFour,
  List,
  CaretRight,
  MagnifyingGlass,
  SortAscending,
  PencilSimple,
  Copy,
  DotsThree,
  FunnelSimple,
  Users,
  User,
  Exam,
  Notebook,
  FileDoc,
  EnvelopeSimple,
  Notepad,
  ChartBar,
  IdentificationCard,
} from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { useDocumentStore } from "../../stores/document";
import { useAuthStore } from "../../stores/auth";
import { useUIStore, type DashboardViewMode, type DashboardSortKey } from "../../stores/ui";
import { useToast } from "../../components/ui/Toast";
import type { ProjectListItem } from "../../types/document";

const spring = { type: "spring" as const, stiffness: 400, damping: 32, mass: 0.8 };
const springFast = { type: "spring" as const, stiffness: 500, damping: 35, mass: 0.6 };
const stagger = { staggerChildren: 0.035, delayChildren: 0.06 };
const itemVariants = {
  hidden: { opacity: 0, y: 8, scale: 0.97 },
  show: { opacity: 1, y: 0, scale: 1, transition: spring },
};

const TEMPLATES = [
  { id: "blank-latex", label: "LaTeX", icon: MathOperations, type: "latex" as const, desc: "Full typesetting" },
  { id: "blank-md", label: "Markdown", icon: FileText, type: "markdown" as const, desc: "Simple writing" },
  { id: "blank-docx", label: "Document", icon: FileDoc, type: "docx" as const, desc: "Rich text (Word)" },
  { id: "paper", label: "Paper", icon: Article, type: "latex" as const, desc: "Academic paper" },
  { id: "essay", label: "Essay", icon: NotePencil, type: "docx" as const, desc: "Structured essay" },
  { id: "research-paper", label: "Research Paper", icon: Article, type: "docx" as const, desc: "With abstract & refs" },
  { id: "resume", label: "Resume", icon: IdentificationCard, type: "docx" as const, desc: "Professional CV" },
  { id: "docx-report", label: "Report", icon: ChartBar, type: "docx" as const, desc: "Formal report" },
  { id: "cover-letter", label: "Cover Letter", icon: EnvelopeSimple, type: "docx" as const, desc: "Job application" },
  { id: "meeting-notes", label: "Meeting Notes", icon: Notepad, type: "docx" as const, desc: "Agenda & action items" },
  { id: "pset", label: "Problem Set", icon: NotePencil, type: "latex" as const, desc: "Homework" },
  { id: "pset-answers", label: "Pset (Answers)", icon: Exam, type: "latex" as const, desc: "With answer blocks" },
  { id: "pset-formal", label: "Pset (Formal)", icon: Notebook, type: "latex" as const, desc: "Header & footer" },
] as const;

const TEMPLATE_CATEGORIES = [
  {
    id: "latex" as const,
    label: "LaTeX",
    ext: ".tex",
    desc: "Typeset papers, problem sets, and formal academic documents with precision.",
    templates: TEMPLATES.filter((t) => t.type === "latex"),
  },
  {
    id: "markdown" as const,
    label: "Markdown",
    ext: ".md",
    desc: "Lightweight markup for notes, documentation, and quick writing.",
    templates: TEMPLATES.filter((t) => t.type === "markdown"),
  },
  {
    id: "docx" as const,
    label: "Document",
    ext: ".docx",
    desc: "Rich text — essays, reports, letters, and general writing.",
    templates: TEMPLATES.filter((t) => t.type === "docx"),
  },
];

type OwnershipTab = "all" | "owned" | "shared";

const SORT_OPTIONS: { id: DashboardSortKey; label: string }[] = [
  { id: "modified", label: "Recently modified" },
  { id: "created", label: "Recently created" },
  { id: "alpha-asc", label: "Name A\u2013Z" },
  { id: "alpha-desc", label: "Name Z\u2013A" },
];

// ── Utility ──

function getRelativeTime(date: Date): string {
  const now = Date.now();
  const diff = now - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function sortProjects(projects: ProjectListItem[], sortKey: DashboardSortKey): ProjectListItem[] {
  const sorted = [...projects];
  switch (sortKey) {
    case "modified":
      return sorted.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
    case "created":
      return sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    case "alpha-asc":
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
    case "alpha-desc":
      return sorted.sort((a, b) => b.name.localeCompare(a.name));
  }
}

// ── Sub-components ──

function ViewToggle({ view, onChange }: { view: DashboardViewMode; onChange: (v: DashboardViewMode) => void }) {
  return (
    <div className="relative flex items-center rounded-[var(--radius-md)] border border-[var(--border-hairline)] bg-[var(--bg-base)] p-0.5">
      <motion.div
        layout
        layoutId="view-toggle-pill"
        className="absolute top-0.5 bottom-0.5 rounded-[var(--radius-sm)] bg-[var(--bg-surface)] shadow-sm border border-[var(--border-hairline)]"
        style={{ left: view === "grid" ? 2 : "50%", width: "calc(50% - 2px)" }}
        transition={springFast}
      />
      {(["grid", "list"] as const).map((v) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={cn(
            "relative z-[1] flex items-center justify-center gap-1 rounded-[var(--radius-sm)] px-2.5 py-1 text-[11px] font-medium",
            "transition-colors duration-100",
            view === v ? "text-[var(--text-primary)]" : "text-[var(--text-muted)]",
          )}
        >
          {v === "grid" ? <SquaresFour size={12} weight="bold" /> : <List size={12} weight="bold" />}
          {v === "grid" ? "Grid" : "List"}
        </button>
      ))}
    </div>
  );
}

function OwnershipTabs({ active, onChange }: { active: OwnershipTab; onChange: (t: OwnershipTab) => void }) {
  const tabs: { id: OwnershipTab; label: string; icon: typeof Folder }[] = [
    { id: "all", label: "All", icon: FunnelSimple },
    { id: "owned", label: "Your Projects", icon: User },
    { id: "shared", label: "Shared with You", icon: Users },
  ];

  return (
    <div className="flex items-center gap-0.5 rounded-[var(--radius-md)] border border-[var(--border-hairline)] bg-[var(--bg-base)] p-0.5">
      {tabs.map((tab) => {
        const isActive = active === tab.id;
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            className={cn(
              "relative flex items-center gap-1 rounded-[var(--radius-sm)] px-2.5 py-1 text-[11px] font-medium",
              "transition-colors duration-100",
              isActive ? "text-[var(--text-primary)]" : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]",
            )}
          >
            {isActive && (
              <motion.div
                layoutId="ownership-tab-pill"
                className="absolute inset-0 rounded-[var(--radius-sm)] bg-[var(--bg-surface)] shadow-sm border border-[var(--border-hairline)]"
                transition={springFast}
              />
            )}
            <span className="relative z-[1]"><Icon size={11} weight={isActive ? "fill" : "regular"} /></span>
            <span className="relative z-[1]">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function SortDropdown({ sortKey, onChange }: { sortKey: DashboardSortKey; onChange: (k: DashboardSortKey) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const currentLabel = SORT_OPTIONS.find((o) => o.id === sortKey)?.label || "Sort";

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-1 rounded-[var(--radius-md)] border border-[var(--border-hairline)] bg-[var(--bg-base)]",
          "px-2 py-1 text-[11px] font-medium text-[var(--text-muted)]",
          "hover:border-[var(--border-subtle)] hover:text-[var(--text-secondary)]",
          "transition-colors duration-100",
        )}
      >
        <SortAscending size={12} />
        {currentLabel}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -4 }}
            transition={springFast}
            className={cn(
              "absolute right-0 top-full mt-1 z-50",
              "w-48 rounded-[var(--radius-lg)] p-1",
              "bg-[var(--bg-surface)] border border-[var(--border-subtle)]",
              "shadow-[var(--shadow-lg)]",
            )}
          >
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                onClick={() => { onChange(opt.id); setOpen(false); }}
                className={cn(
                  "flex w-full items-center gap-2 rounded-[var(--radius-md)] px-2.5 py-1.5 text-[12px] text-left",
                  sortKey === opt.id
                    ? "text-[var(--text-primary)] bg-[var(--bg-surface-hover)]"
                    : "text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]",
                  "transition-colors duration-75",
                )}
              >
                {opt.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ContextMenu({
  x,
  y,
  onClose,
  onOpen,
  onRename,
  onDuplicate,
  onDelete,
}: {
  x: number;
  y: number;
  onClose: () => void;
  onOpen: () => void;
  onRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const escHandler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", escHandler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", escHandler);
    };
  }, [onClose]);

  const items = [
    { label: "Open", icon: <CaretRight size={13} />, action: onOpen },
    { label: "Rename", icon: <PencilSimple size={13} />, action: onRename },
    { label: "Duplicate", icon: <Copy size={13} />, action: onDuplicate },
    null,
    { label: "Delete", icon: <Trash size={13} />, action: onDelete, destructive: true },
  ];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.92 }}
      transition={{ duration: 0.12 }}
      className={cn(
        "fixed z-[60] w-44 rounded-[var(--radius-lg)] p-1",
        "bg-[var(--bg-surface)] border border-[var(--border-subtle)]",
        "shadow-[var(--shadow-xl)]",
      )}
      style={{ top: y, left: x }}
    >
      {items.map((item, i) =>
        item === null ? (
          <div key={i} className="my-0.5 h-px bg-[var(--border-hairline)]" />
        ) : (
          <button
            key={item.label}
            onClick={() => { item.action(); onClose(); }}
            className={cn(
              "flex w-full items-center gap-2 rounded-[var(--radius-md)] px-2.5 py-1.5 text-[12px] text-left",
              "transition-colors duration-75",
              item.destructive
                ? "text-[var(--color-soft-red)] hover:bg-[var(--color-soft-red)]/10"
                : "text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]",
            )}
          >
            <span className={item.destructive ? "text-[var(--color-soft-red)]" : "text-[var(--text-muted)]"}>{item.icon}</span>
            {item.label}
          </button>
        ),
      )}
    </motion.div>
  );
}

function DeleteConfirmDialog({
  open,
  projectName,
  onCancel,
  onConfirm,
  isDeleting,
}: {
  open: boolean;
  projectName: string;
  onCancel: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onCancel]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={onCancel}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            className={cn(
              "relative z-10 w-full max-w-sm",
              "rounded-[var(--radius-xl)] border border-[var(--border-subtle)]",
              "bg-[var(--bg-surface)] shadow-[var(--shadow-xl)]",
              "p-5",
            )}
            initial={{ scale: 0.95, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 12 }}
            transition={spring}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-soft-red)]/10 mb-3">
              <Trash size={18} className="text-[var(--color-soft-red)]" />
            </div>
            <h2 className="text-[15px] font-semibold text-[var(--text-primary)]" style={{ fontFamily: "var(--font-display)" }}>
              Delete project?
            </h2>
            <p className="mt-1.5 text-[12px] text-[var(--text-muted)] leading-relaxed">
              <span className="font-medium text-[var(--text-secondary)]">{projectName}</span> and all its documents will be permanently deleted. This cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-1.5">
              <motion.button
                onClick={onCancel}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className={cn(
                  "rounded-[var(--radius-md)] px-3 py-1.5 text-[12px] font-medium text-[var(--text-secondary)]",
                  "hover:bg-[var(--bg-surface-hover)]",
                  "transition-colors duration-100",
                )}
              >
                Cancel
              </motion.button>
              <motion.button
                onClick={onConfirm}
                disabled={isDeleting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className={cn(
                  "rounded-[var(--radius-md)] px-3.5 py-1.5 text-[12px] font-semibold",
                  "bg-[var(--color-soft-red)] text-white",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  "transition-opacity duration-100",
                )}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function RenameDialog({
  open,
  initialName,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  initialName: string;
  onCancel: () => void;
  onConfirm: (name: string) => void;
}) {
  const [name, setName] = useState(initialName);

  useEffect(() => {
    if (open) setName(initialName);
  }, [open, initialName]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
          <motion.div
            className={cn(
              "relative z-10 w-full max-w-sm",
              "rounded-[var(--radius-xl)] border border-[var(--border-subtle)]",
              "bg-[var(--bg-surface)] shadow-[var(--shadow-xl)] p-5",
            )}
            initial={{ scale: 0.95, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 12 }}
            transition={spring}
          >
            <h2 className="text-[15px] font-semibold text-[var(--text-primary)]" style={{ fontFamily: "var(--font-display)" }}>
              Rename project
            </h2>
            <div className="mt-3">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                className={cn(
                  "w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)]",
                  "bg-[var(--bg-base)] px-3 py-2 text-[13px] text-[var(--text-primary)]",
                  "placeholder:text-[var(--text-muted)]",
                  "outline-none focus:border-[var(--color-lever-orange)]",
                  "transition-colors duration-150",
                )}
                onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) onConfirm(name.trim()); if (e.key === "Escape") onCancel(); }}
              />
            </div>
            <div className="mt-4 flex justify-end gap-1.5">
              <motion.button onClick={onCancel} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                className="rounded-[var(--radius-md)] px-3 py-1.5 text-[12px] font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)] transition-colors duration-100"
              >
                Cancel
              </motion.button>
              <motion.button
                onClick={() => name.trim() && onConfirm(name.trim())}
                disabled={!name.trim() || name.trim() === initialName}
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                className={cn(
                  "rounded-[var(--radius-md)] px-3.5 py-1.5 text-[12px] font-semibold",
                  "bg-[var(--text-primary)] text-[var(--text-inverse)]",
                  "disabled:opacity-30 disabled:cursor-not-allowed",
                  "transition-opacity duration-100",
                )}
              >
                Rename
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ProjectCard({
  project,
  onClick,
  onDelete,
  onContextMenu,
}: {
  project: ProjectListItem;
  onClick: () => void;
  onDelete: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}) {
  const date = new Date(project.updated_at || project.created_at);
  const relative = getRelativeTime(date);

  return (
    <motion.div
      variants={itemVariants}
      layout
      onClick={onClick}
      onContextMenu={(e) => { e.preventDefault(); onContextMenu(e); }}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      transition={springFast}
      className={cn(
        "group relative flex flex-col items-start rounded-[var(--radius-lg)] p-3.5",
        "border border-[var(--border-hairline)]",
        "bg-[var(--bg-surface)] hover:bg-[var(--bg-surface-hover)]",
        "hover:border-[var(--border-subtle)] hover:shadow-[var(--shadow-sm)]",
        "cursor-pointer text-left",
      )}
    >
      <div className="flex items-center justify-between w-full mb-2.5">
        <div className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] bg-[var(--bg-base)] text-[var(--text-muted)]">
          <Folder size={15} weight="fill" />
        </div>
        <div className="flex items-center gap-0.5">
          <motion.button
            onClick={(e) => { e.stopPropagation(); onContextMenu(e); }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.85 }}
            className={cn(
              "flex items-center justify-center rounded-[var(--radius-sm)] p-1",
              "text-[var(--text-muted)] opacity-0 group-hover:opacity-100",
              "hover:bg-[var(--bg-surface-hover)]",
              "transition-all duration-100",
            )}
            aria-label="Project options"
          >
            <DotsThree size={14} weight="bold" />
          </motion.button>
          <motion.button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.85 }}
            className={cn(
              "flex items-center justify-center rounded-[var(--radius-sm)] p-1",
              "text-[var(--text-muted)] opacity-0 group-hover:opacity-100",
              "hover:bg-[var(--color-soft-red)]/10 hover:text-[var(--color-soft-red)]",
              "transition-all duration-100",
            )}
            aria-label="Delete project"
          >
            <Trash size={13} />
          </motion.button>
        </div>
      </div>
      <div className="w-full min-w-0">
        <div className="text-[13px] font-semibold text-[var(--text-primary)] truncate leading-tight">
          {project.name}
        </div>
        {project.description && (
          <div className="text-[11px] text-[var(--text-muted)] truncate mt-0.5 leading-tight">
            {project.description}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between w-full mt-2.5">
        <span className="flex items-center gap-1 text-[10px] text-[var(--text-muted)]">
          <Clock size={10} />
          {relative}
        </span>
        <CaretRight
          size={12}
          className="text-[var(--text-muted)] opacity-0 group-hover:opacity-60 transition-opacity duration-100"
        />
      </div>
    </motion.div>
  );
}

function ProjectRow({
  project,
  onClick,
  onDelete,
  onContextMenu,
}: {
  project: ProjectListItem;
  onClick: () => void;
  onDelete: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}) {
  const date = new Date(project.updated_at || project.created_at);
  const relative = getRelativeTime(date);

  return (
    <motion.div
      variants={itemVariants}
      layout
      onClick={onClick}
      onContextMenu={(e) => { e.preventDefault(); onContextMenu(e); }}
      transition={springFast}
      className={cn(
        "group flex items-center gap-3 w-full px-3.5 py-2.5 text-left",
        "border-b border-[var(--border-hairline)] last:border-b-0",
        "hover:bg-[var(--bg-surface-hover)]",
        "active:bg-[var(--bg-base)]",
        "transition-colors duration-75 cursor-pointer",
      )}
    >
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--radius-sm)] bg-[var(--bg-base)] text-[var(--text-muted)]">
        <Folder size={14} weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-[13px] font-medium text-[var(--text-primary)] truncate block leading-tight">
          {project.name}
        </span>
        {project.description && (
          <span className="text-[11px] text-[var(--text-muted)] truncate block leading-tight">
            {project.description}
          </span>
        )}
      </div>
      <span className="flex items-center gap-1 text-[11px] text-[var(--text-muted)] shrink-0">
        <Clock size={11} />
        {relative}
      </span>
      <motion.button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.85 }}
        className={cn(
          "flex items-center justify-center rounded-[var(--radius-sm)] p-1",
          "text-[var(--text-muted)] opacity-0 group-hover:opacity-100",
          "hover:bg-[var(--color-soft-red)]/10 hover:text-[var(--color-soft-red)]",
          "transition-all duration-100",
        )}
        aria-label="Delete project"
      >
        <Trash size={13} />
      </motion.button>
    </motion.div>
  );
}

function NewProjectPopover({
  open,
  onClose,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (templateId: string) => void;
}) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: -4 }}
            transition={springFast}
            className={cn(
              "absolute right-0 top-full mt-1 z-50",
              "w-52 rounded-[var(--radius-lg)] p-1.5",
              "bg-[var(--bg-surface)] border border-[var(--border-subtle)]",
              "shadow-[var(--shadow-lg)]",
              "max-h-[420px] overflow-y-auto",
            )}
          >
            {TEMPLATE_CATEGORIES.map((category, catIdx) => (
              <div key={category.id}>
                {catIdx > 0 && <div className="my-1 h-px bg-[var(--border-hairline)]" />}
                <div className="flex items-center gap-2 px-2 pt-1.5 pb-1">
                  <span
                    className="text-[9px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]"
                    style={{ fontFamily: "var(--font-mono)" }}
                  >
                    {category.label}
                  </span>
                  <span
                    className="text-[8px] text-[var(--text-muted)] opacity-50"
                    style={{ fontFamily: "var(--font-mono)" }}
                  >
                    {category.ext}
                  </span>
                </div>
                {category.templates.map((t) => {
                  const Icon = t.icon;
                  return (
                    <motion.button
                      key={t.id}
                      onClick={() => { onSelect(t.id); onClose(); }}
                      whileHover={{ x: 2 }}
                      transition={springFast}
                      className={cn(
                        "flex items-center gap-2.5 w-full rounded-[var(--radius-md)] px-2 py-1.5 text-left",
                        "text-[12px] text-[var(--text-secondary)]",
                        "hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]",
                        "transition-colors duration-75",
                      )}
                    >
                      <div className="flex h-6 w-6 items-center justify-center rounded-[3px] bg-[var(--bg-base)] text-[var(--text-muted)]">
                        <Icon size={12} />
                      </div>
                      <span className="font-medium truncate">{t.label}</span>
                    </motion.button>
                  );
                })}
              </div>
            ))}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function CreateProjectDialog({
  open,
  onClose,
  initialTemplate,
}: {
  open: boolean;
  onClose: () => void;
  initialTemplate?: string;
}) {
  const [name, setName] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(initialTemplate || "blank-latex");
  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();
  const { createProject, createDocument } = useDocumentStore();
  const toast = useToast();

  useEffect(() => {
    if (initialTemplate) setSelectedTemplate(initialTemplate);
  }, [initialTemplate]);

  useEffect(() => {
    if (!open) {
      setName("");
      setIsCreating(false);
    }
  }, [open]);

  const handleCreate = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed || isCreating) return;
    if (trimmed.length > 200) return;
    setIsCreating(true);
    try {
      const template = TEMPLATES.find((t) => t.id === selectedTemplate);
      const project = await createProject(name.trim());
      const docType = template?.type || "latex";
      const filename = docType === "latex" ? "main.tex" : docType === "docx" ? "main.docx" : "main.md";
      const blankIds = new Set(["blank-latex", "blank-md", "blank-docx"]);
      const templateId = !blankIds.has(selectedTemplate) ? selectedTemplate : undefined;
      await createDocument(project.id, filename, docType, templateId);
      toast.success("Project created", `"${name.trim()}" is ready`);
      onClose();
      navigate(`/project/${project.id}`);
    } catch {
      toast.error("Creation failed", "Could not create the project. Please try again.");
    } finally {
      setIsCreating(false);
    }
  }, [name, selectedTemplate, isCreating, createProject, createDocument, navigate, onClose, toast]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape" && !isCreating) onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose, isCreating]);

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => { if (!isCreating) onClose(); }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            className={cn(
              "relative z-10 w-full max-w-sm",
              "rounded-[var(--radius-xl)] border border-[var(--border-subtle)]",
              "bg-[var(--bg-surface)] shadow-[var(--shadow-xl)]",
              "p-5",
            )}
            initial={{ scale: 0.95, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 12 }}
            transition={spring}
          >
            <h2 className="text-[15px] font-semibold text-[var(--text-primary)]" style={{ fontFamily: "var(--font-display)" }}>
              New Project
            </h2>

            <div className="mt-3.5">
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                Name
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My project"
                maxLength={200}
                autoFocus
                disabled={isCreating}
                className={cn(
                  "w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)]",
                  "bg-[var(--bg-base)] px-3 py-2 text-[13px] text-[var(--text-primary)]",
                  "placeholder:text-[var(--text-muted)]",
                  "outline-none focus:border-[var(--color-lever-orange)]",
                  "transition-colors duration-150",
                )}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleCreate(); } }}
              />
            </div>

            <div className="mt-3">
              <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                Template
              </label>
              <div className="rounded-[var(--radius-md)] bg-[var(--bg-base)] border border-[var(--border-hairline)] max-h-[260px] overflow-y-auto">
                {TEMPLATE_CATEGORIES.map((category, catIdx) => (
                  <div key={category.id} className={catIdx > 0 ? "border-t border-[var(--border-hairline)]" : ""}>
                    <div className="sticky top-0 bg-[var(--bg-base)] px-2.5 pt-2 pb-1 z-[1]">
                      <span
                        className="text-[9px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]"
                        style={{ fontFamily: "var(--font-mono)" }}
                      >
                        {category.label}
                        <span className="ml-1.5 font-normal opacity-50">{category.ext}</span>
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-1 px-1 pb-1.5">
                      {category.templates.map((t) => {
                        const TIcon = t.icon;
                        const isSelected = selectedTemplate === t.id;
                        return (
                          <button
                            key={t.id}
                            onClick={() => setSelectedTemplate(t.id)}
                            className={cn(
                              "relative flex items-center gap-1.5 rounded-[var(--radius-sm)] px-2.5 py-1.5 text-[12px] font-medium",
                              "transition-colors duration-75 text-left",
                              isSelected ? "text-[var(--text-primary)]" : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]",
                            )}
                          >
                            {isSelected && (
                              <motion.div
                                layoutId="template-select"
                                className="absolute inset-0 rounded-[var(--radius-sm)] bg-[var(--bg-surface)] shadow-sm border border-[var(--border-hairline)]"
                                transition={springFast}
                              />
                            )}
                            <span className="relative z-[1]"><TIcon size={13} /></span>
                            <span className="relative z-[1]">{t.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-1.5">
              <motion.button
                onClick={onClose}
                disabled={isCreating}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className={cn(
                  "rounded-[var(--radius-md)] px-3 py-1.5 text-[12px] font-medium text-[var(--text-secondary)]",
                  "hover:bg-[var(--bg-surface-hover)]",
                  "disabled:opacity-30 disabled:cursor-not-allowed",
                  "transition-colors duration-100",
                )}
              >
                Cancel
              </motion.button>
              <motion.button
                onClick={handleCreate}
                disabled={!name.trim() || isCreating}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className={cn(
                  "rounded-[var(--radius-md)] px-3.5 py-1.5 text-[12px] font-semibold",
                  "bg-[var(--text-primary)] text-[var(--text-inverse)]",
                  "disabled:opacity-30 disabled:cursor-not-allowed",
                  "transition-opacity duration-100",
                )}
              >
                {isCreating ? "Creating..." : "Create"}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function LoadingSkeletonGrid() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex flex-col gap-2.5 rounded-[var(--radius-lg)] border border-[var(--border-hairline)] bg-[var(--bg-surface)] p-3.5 animate-pulse">
          <div className="h-8 w-8 rounded-[var(--radius-md)] bg-[var(--bg-base)]" />
          <div className="space-y-1">
            <div className="h-3.5 w-3/4 rounded bg-[var(--bg-base)]" />
            <div className="h-2.5 w-1/2 rounded bg-[var(--bg-base)]" />
          </div>
          <div className="h-2.5 w-16 rounded bg-[var(--bg-base)] mt-auto" />
        </div>
      ))}
    </div>
  );
}

function LoadingSkeletonList() {
  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] overflow-hidden">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center gap-3 px-3.5 py-2.5 animate-pulse border-b border-[var(--border-hairline)] last:border-b-0">
          <div className="h-7 w-7 rounded-[var(--radius-sm)] bg-[var(--bg-base)]" />
          <div className="flex-1 space-y-1">
            <div className="h-3.5 w-1/3 rounded bg-[var(--bg-base)]" />
          </div>
          <div className="h-3 w-16 rounded bg-[var(--bg-base)]" />
        </div>
      ))}
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={spring}
      className="flex flex-col items-center justify-center py-12 text-center"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-soft-red)]/10">
        <WifiSlash size={18} className="text-[var(--color-soft-red)]" />
      </div>
      <p className="mt-2.5 text-[13px] font-medium text-[var(--text-primary)]">Can't reach the server</p>
      <p className="mt-0.5 max-w-xs text-[11px] text-[var(--text-muted)]">{message}</p>
      <motion.button
        onClick={onRetry}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        className={cn(
          "mt-3 flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1.5 text-[11px] font-medium",
          "border border-[var(--border-default)] text-[var(--text-primary)]",
          "hover:bg-[var(--bg-surface-hover)]",
          "transition-colors duration-100",
        )}
      >
        <ArrowClockwise size={12} />
        Retry
      </motion.button>
    </motion.div>
  );
}

function EmptyState({ onSelectTemplate }: { onSelectTemplate: (id: string) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      className="pt-2 pb-12"
    >
      {/* Editorial header */}
      <div className="mb-8">
        <div className="h-[2px] bg-[var(--text-primary)] mb-5" />
        <h2
          className="text-[28px] font-bold tracking-[-0.045em] text-[var(--text-primary)] leading-[0.95]"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Start writing
        </h2>
        <p className="mt-2.5 text-[12px] text-[var(--text-muted)]">
          Pick a format and template to begin. You can always add more files later.
        </p>
      </div>

      {/* Three-column category grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
        {TEMPLATE_CATEGORIES.map((category, catIdx) => (
          <motion.div
            key={category.id}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 + catIdx * 0.07, ...spring }}
            className={cn(
              "py-4 md:pr-5",
              catIdx > 0 && "md:pl-5",
              "border-b md:border-b-0 last:border-b-0",
              catIdx < TEMPLATE_CATEGORIES.length - 1 && "md:border-r",
              "border-[var(--border-default)]",
            )}
          >
            {/* Category header */}
            <div className="mb-3.5">
              <div className="flex items-center gap-2.5 mb-1.5">
                <span
                  className="text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--text-primary)]"
                  style={{ fontFamily: "var(--font-mono)" }}
                >
                  {category.label}
                </span>
                <span
                  className="text-[9px] px-1.5 py-[1px] rounded-[3px] bg-[var(--bg-surface)] border border-[var(--border-hairline)] text-[var(--text-muted)]"
                  style={{ fontFamily: "var(--font-mono)" }}
                >
                  {category.ext}
                </span>
              </div>
              <p className="text-[11px] text-[var(--text-muted)] leading-relaxed pr-2">
                {category.desc}
              </p>
            </div>

            {/* Template list */}
            <div className="space-y-0.5">
              {category.templates.map((t, tIdx) => {
                const Icon = t.icon;
                return (
                  <motion.button
                    key={t.id}
                    onClick={() => onSelectTemplate(t.id)}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 + catIdx * 0.07 + tIdx * 0.03 }}
                    whileHover={{ x: 2 }}
                    className={cn(
                      "flex items-center gap-2.5 w-full rounded-[var(--radius-md)] px-2 py-2 text-left",
                      "group/tpl cursor-pointer",
                      "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75",
                    )}
                  >
                    <div className={cn(
                      "flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--radius-sm)]",
                      "bg-[var(--bg-surface)] border border-[var(--border-hairline)]",
                      "text-[var(--text-muted)] group-hover/tpl:text-[var(--text-primary)]",
                      "group-hover/tpl:border-[var(--border-subtle)]",
                      "transition-colors duration-100",
                    )}>
                      <Icon size={13} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[12px] font-semibold text-[var(--text-secondary)] group-hover/tpl:text-[var(--text-primary)] transition-colors block truncate leading-tight">
                        {t.label}
                      </span>
                      <span className="text-[10px] text-[var(--text-muted)] block truncate leading-tight">
                        {t.desc}
                      </span>
                    </div>
                    <CaretRight
                      size={11}
                      className="text-[var(--text-muted)] opacity-0 group-hover/tpl:opacity-60 transition-opacity shrink-0"
                    />
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

// ── Main Component ──

export function DashboardPage() {
  useDocumentTitle("Dashboard");
  const { projects, isLoading, fetchError, fetchProjects, deleteProject, renameProject } = useDocumentStore();
  const currentUser = useAuthStore((s) => s.user);
  const devMode = useAuthStore((s) => s.devMode);
  const currentUserId = currentUser?.id || (devMode ? "dev-user" : "");
  const viewMode = useUIStore((s) => s.dashboardViewMode);
  const setViewMode = useUIStore((s) => s.setDashboardViewMode);
  const sortKey = useUIStore((s) => s.dashboardSortKey);
  const setSortKey = useUIStore((s) => s.setDashboardSortKey);
  const toast = useToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(false);
  const [createTemplate, setCreateTemplate] = useState<string | undefined>();
  const [showNewPopover, setShowNewPopover] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [ownershipTab, setOwnershipTab] = useState<OwnershipTab>("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<ProjectListItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Rename dialog
  const [renameTarget, setRenameTarget] = useState<ProjectListItem | null>(null);

  // Context menu
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; project: ProjectListItem } | null>(null);

  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchProjects().finally(() => setHasFetched(true));
  }, [fetchProjects]);

  useEffect(() => {
    if (searchParams.get("create") === "true") {
      setShowCreate(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleTemplateSelect = useCallback((templateId: string) => {
    setCreateTemplate(templateId);
    setShowCreate(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteProject(deleteTarget.id);
      toast.success("Project deleted", `"${deleteTarget.name}" was removed`);
    } catch {
      toast.error("Delete failed", "Could not delete the project");
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteProject, toast]);

  const handleRename = useCallback(async (newName: string) => {
    if (!renameTarget) return;
    try {
      await renameProject(renameTarget.id, newName);
      toast.success("Project renamed", `Now called "${newName}"`);
    } catch {
      toast.error("Rename failed", "Could not rename the project");
    }
    setRenameTarget(null);
  }, [renameTarget, renameProject, toast]);

  const handleDuplicate = useCallback(async (project: ProjectListItem) => {
    try {
      const { api: apiClient } = await import("../../lib/api");
      const newProject = await apiClient.post<ProjectListItem>(`/api/v1/projects/${project.id}/duplicate`);
      useDocumentStore.getState().fetchProjects();
      toast.success("Project duplicated", `"${newProject.name}" created with all documents`);
    } catch {
      toast.error("Duplicate failed", "Could not duplicate the project");
    }
  }, [toast]);

  // Filtered and sorted projects
  const displayProjects = useMemo(() => {
    let filtered = projects;

    if (ownershipTab === "owned") {
      filtered = filtered.filter((p) => p.owner_id === currentUserId);
    } else if (ownershipTab === "shared") {
      filtered = filtered.filter((p) => p.owner_id !== currentUserId);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) => p.name.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q),
      );
    }
    return sortProjects(filtered, sortKey);
  }, [projects, ownershipTab, currentUserId, searchQuery, sortKey]);

  const showLoading = !hasFetched && isLoading;
  const showError = hasFetched && fetchError;
  const showEmpty = hasFetched && !fetchError && projects.length === 0;
  const showProjects = hasFetched && !fetchError && projects.length > 0;
  const showNoResults = showProjects && displayProjects.length === 0;

  return (
    <LayoutGroup>
      <div className="p-4 pb-8">
        {/* Header */}
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-[17px] font-semibold text-[var(--text-primary)] tracking-[-0.03em]"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Projects
              </h1>
              <p className="text-[11px] text-[var(--text-muted)]">
                {showProjects
                  ? `${displayProjects.length} of ${projects.length} project${projects.length !== 1 ? "s" : ""}`
                  : "Documents, problem sets, and papers"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <motion.button
                  onClick={() => setShowNewPopover(!showNewPopover)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.96 }}
                  className={cn(
                    "flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1.5 text-[12px] font-semibold",
                    "bg-[var(--text-primary)] text-[var(--text-inverse)]",
                    "transition-opacity duration-100",
                  )}
                >
                  <Plus size={13} weight="bold" />
                  New
                </motion.button>
                <NewProjectPopover
                  open={showNewPopover}
                  onClose={() => setShowNewPopover(false)}
                  onSelect={handleTemplateSelect}
                />
              </div>
            </div>
          </div>

          {/* Controls row */}
          <AnimatePresence>
            {showProjects && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto", overflow: "visible", transitionEnd: { overflow: "visible" } }}
                exit={{ opacity: 0, height: 0, overflow: "hidden" }}
                transition={{ ...spring, opacity: { duration: 0.15 } }}
                style={{ overflow: "hidden" }}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <OwnershipTabs active={ownershipTab} onChange={setOwnershipTab} />
                  <div className="flex-1" />

                  {/* Search */}
                  <div className={cn(
                    "flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-hairline)] bg-[var(--bg-base)]",
                    "px-2 py-1 text-[11px] w-48",
                    "focus-within:border-[var(--border-subtle)]",
                    "transition-colors duration-100",
                  )}>
                    <MagnifyingGlass size={12} className="text-[var(--text-muted)] shrink-0" />
                    <input
                      ref={searchRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Filter projects..."
                      className="flex-1 bg-transparent text-[11px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none min-w-0"
                    />
                    <AnimatePresence>
                      {searchQuery && (
                        <motion.button
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={springFast}
                          onClick={() => setSearchQuery("")}
                          className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                        >
                          <span className="text-[10px]">&#x2715;</span>
                        </motion.button>
                      )}
                    </AnimatePresence>
                  </div>

                  <SortDropdown sortKey={sortKey} onChange={setSortKey} />
                  <ViewToggle view={viewMode} onChange={setViewMode} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {showLoading && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              {viewMode === "grid" ? <LoadingSkeletonGrid /> : <LoadingSkeletonList />}
            </motion.div>
          )}

          {showError && (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={spring}
            >
              <ErrorState
                message={fetchError!}
                onRetry={() => {
                  setHasFetched(false);
                  fetchProjects().finally(() => setHasFetched(true));
                }}
              />
            </motion.div>
          )}

          {showEmpty && (
            <motion.div
              key="empty"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={spring}
            >
              <EmptyState
                onSelectTemplate={handleTemplateSelect}
              />
            </motion.div>
          )}

          {showNoResults && (
            <motion.div
              key="no-results"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={spring}
              className="flex flex-col items-center justify-center py-12 text-center"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-base)]">
                <MagnifyingGlass size={18} className="text-[var(--text-muted)]" />
              </div>
              <p className="mt-2.5 text-[13px] font-medium text-[var(--text-primary)]">No matching projects</p>
              <p className="mt-0.5 text-[11px] text-[var(--text-muted)]">
                Try a different search term or{" "}
                <button onClick={() => setSearchQuery("")} className="text-[var(--color-lever-orange)] hover:underline">
                  clear filter
                </button>
              </p>
            </motion.div>
          )}

          {showProjects && !showNoResults && viewMode === "grid" && (
            <motion.div
              key="grid"
              variants={{ show: { transition: stagger } }}
              initial="hidden"
              animate="show"
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5"
            >
              {displayProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onClick={() => navigate(`/project/${project.id}`)}
                  onDelete={() => setDeleteTarget(project)}
                  onContextMenu={(e) => setContextMenu({ x: e.clientX, y: e.clientY, project })}
                />
              ))}
            </motion.div>
          )}

          {showProjects && !showNoResults && viewMode === "list" && (
            <motion.div
              key="list"
              variants={{ show: { transition: stagger } }}
              initial="hidden"
              animate="show"
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] overflow-hidden"
            >
              {displayProjects.map((project) => (
                <ProjectRow
                  key={project.id}
                  project={project}
                  onClick={() => navigate(`/project/${project.id}`)}
                  onDelete={() => setDeleteTarget(project)}
                  onContextMenu={(e) => setContextMenu({ x: e.clientX, y: e.clientY, project })}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Dialogs */}
      <CreateProjectDialog
        open={showCreate}
        onClose={() => { setShowCreate(false); setCreateTemplate(undefined); }}
        initialTemplate={createTemplate}
      />

      <DeleteConfirmDialog
        open={!!deleteTarget}
        projectName={deleteTarget?.name || ""}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />

      <RenameDialog
        open={!!renameTarget}
        initialName={renameTarget?.name || ""}
        onCancel={() => setRenameTarget(null)}
        onConfirm={handleRename}
      />

      {/* Context menu */}
      <AnimatePresence>
        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            onClose={() => setContextMenu(null)}
            onOpen={() => navigate(`/project/${contextMenu.project.id}`)}
            onRename={() => setRenameTarget(contextMenu.project)}
            onDuplicate={() => handleDuplicate(contextMenu.project)}
            onDelete={() => setDeleteTarget(contextMenu.project)}
          />
        )}
      </AnimatePresence>
    </LayoutGroup>
  );
}
